import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditorBookingItem } from '../editor-booking-item/editor-booking-item.model';
import { EditorBookingItemTreeStore } from '../editor-booking-item-tree/editor-booking-item-tree.store';


export interface EditorBookingItemBulkName {
  id: string;
  name: string;
  level: number;
  isLastSibling: boolean;
}


@Injectable()
export class EditorBookingItemBulkNameEditStore {
  private readonly _replacingBulkNameList$ = new BehaviorSubject<EditorBookingItemBulkName[] | undefined>(undefined);
  private readonly _replacingNameListForm$ = new BehaviorSubject<FormArray | undefined>(undefined);

  readonly replacingBulkNameList$ = this._replacingBulkNameList$.asObservable();
  readonly replacingNameListForm$ = this._replacingNameListForm$.asObservable();

  constructor(
    private fb: FormBuilder,
    private itemTreeStore: EditorBookingItemTreeStore
  ) {
    this.itemTreeStore.editingItemNameList$.pipe(
      filter(editingItemNameList => editingItemNameList !== undefined)
    ).subscribe(editingItemNameList =>
      this.initializeFromItemList(editingItemNameList)
    );
  }

  private get replacingBulkNameList(): EditorBookingItemBulkName[] | undefined {
    return this._replacingBulkNameList$.getValue();
  }

  private set replacingBulkNameList(value: EditorBookingItemBulkName[] | undefined) {
    this._replacingBulkNameList$.next(value);
  }

  private get replacingNameListForm(): FormArray | undefined {
    return this._replacingNameListForm$.getValue();
  }

  private set replacingNameListForm(value: FormArray | undefined) {
    this._replacingNameListForm$.next(value);
  }

  initialize(itemBulkNameList: EditorBookingItemBulkName[]) {
    this.replacingBulkNameList = itemBulkNameList;
    this.replacingNameListForm = this.buildForm(itemBulkNameList);
  }

  terminate() {
    this.replacingNameListForm = undefined;
    this.replacingBulkNameList = undefined;
  }

  initializeFromItemList(itemList: EditorBookingItem[]) {
    const itemBulkNameList = itemList.map(item => {
      return {
        id: item.id,
        name: item.name,
        level: item.level - itemList[0].level,
        isLastSibling: item.isLastSibling
      };
    });
    this.initialize(itemBulkNameList);
  }

  save() {
    const replacingNameListForm = this.replacingNameListForm;
    const replacingBulkNameList = this.replacingBulkNameList;

    const replacingFilteredBulkNameList: EditorBookingItemBulkName[] = replacingNameListForm.controls
      .map((nameControl, nameControlIndex) =>
        [nameControl, replacingBulkNameList[nameControlIndex]]
      )
      .filter(([nameControl, _replacingItemBulkName]: [AbstractControl, EditorBookingItemBulkName]) =>
        nameControl.dirty
      )
      .map(([nameControl, replacingItemBulkName]: [AbstractControl, EditorBookingItemBulkName]) => {
        return {
          id: replacingItemBulkName.id,
          name: nameControl.value,
          level: replacingItemBulkName.level,
          isLastSibling: replacingItemBulkName.isLastSibling
        };
      });

    this.itemTreeStore.replaceItemNameList(replacingFilteredBulkNameList);
  }

  private buildForm(bulkNameList: EditorBookingItemBulkName[]): FormArray {
    return this.fb.array(bulkNameList.map(
      bulkName => this.fb.control(bulkName.name)
    ));
  }
}
